import {
  Button, Icon, MenuItem, TextField
} from '@mui/material';
import {
  useCallback, useMemo, useReducer,
  type ReactElement
} from 'react';
import { v4 as uuid } from 'uuid';

import {
  toCamelCase, toSnakeCase, toTitle
} from '../utils';

interface FilterComponentProps {
  columnProps: any; // TODO: colump prop type
  handleCancel: () => void;
  handleFilter: (a: any) => void;
  initFilter: any; // TODO: filter type
  properties: any;
}

interface Condition {
  id: string;
  input: string;
  value: string | boolean | number;
}

interface Field {
  label: string;
  value: string | number;
}

function reducer(filter, action): any {
  const { type, payload } = action;

  switch (type) {
    case 'ADD_FILTER': {
      const id = uuid();

      return {
        ...filter,
        [id]: { id }
      };
    }
    case 'SET_FILTER': {
      const { id, input, value } = payload;
      const newFilter = { ...filter };

      newFilter[id][input] = value;

      return newFilter;
    }
    case 'DELETE_FILTER': {
      const newFilter = { ...filter };
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete newFilter[payload.id];

      return newFilter;
    }
    default:
      return {};
  }
}

function validateCondition(condition): boolean {
  const { field, operator, value } = condition;

  return field !== undefined && operator !== undefined && value !== undefined;
}

function FilterComponent({
  columnProps,
  handleCancel,
  handleFilter,
  initFilter,
  properties
}: FilterComponentProps): ReactElement {
  const [ filter, dispatch ] = useReducer(reducer, initFilter || {});

  const handleAddCondition = useCallback(function handleAddCondition() {
    dispatch({ type: 'ADD_FILTER' });
  }, []);

  const handleChangeCondition = useCallback(function handleChangeCondition(event) {
    const { name, value } = event.target;
    const [ id, input ] = name.split('__');

    dispatch({
      type: 'SET_FILTER',
      payload: { id, input, value } satisfies Condition
    });
  }, []);

  const handleRemoveCondition = useCallback(function handleRemoveCondition(event) {
    if (event.target.dataset.action === 'remove')
      dispatch({
        type: 'DELETE_FILTER',
        payload: { id: event.target.dataset.id }
      });
  }, []);

  // TODO: add to reducer
  const handleApply = useCallback(
    function handleApply() {
      handleFilter(filter);
      handleCancel();
    },
    [ handleCancel, handleFilter, filter ]
  );

  const handleClear = useCallback(
    function handleApply() {
      handleFilter({});
      handleCancel();
    },
    [ handleCancel, handleFilter ]
  );

  const disabled = useMemo(
    () => !Object.keys(filter).length || !Object.values(filter).every(validateCondition),
    [ filter ]
  );

  return (
    <div className="table-filter-component box-shadow">
      <div className="header">
        <Button className="bm-btn-add" onClick={handleAddCondition} variant="contained">
          <Icon className="icon-add-filter">add</Icon> Add Filter
        </Button>
      </div>
      <div className="conditions" onClick={handleRemoveCondition}>
        { Object.values(filter).map((condition: Condition) => (
          <FilterCondition
            key={condition.id}
            columnProps={columnProps}
            condition={condition}
            onChange={handleChangeCondition}
            properties={properties}
          />
        )) }
      </div>
      <div className="footer">
        <Button onClick={handleClear} variant="contained">
          Clear
        </Button>
        <Button onClick={handleCancel} variant="contained">
          Cancel
        </Button>
        <Button disabled={disabled} onClick={handleApply} variant="contained">
          Apply
        </Button>
      </div>
    </div>
  );
}

function FilterCondition(props): ReactElement {
  return (
    <div className="condition">
      <FieldInput {...props} />
      <OperatorInput {...props} />
      <ValueInput {...props} />
      <DeleteButton {...props} />
    </div>
  );
}

function FieldInput(props): ReactElement {
  const columnProps = props.columnProps;
  const condition = props.condition || {};
  const onChange = props.onChange;

  const value = condition.field || '_';

  const fields = useMemo(() => {
    const fields: Field[] = [];

    columnProps.forEach((property) => {
      const key = property.key;

      if (property.filterable)
        fields.push({
          label: property.label || toTitle(key),
          value: property.columnName || toSnakeCase(key)
        });
    });

    return fields;
  }, [ columnProps ]);

  return (
    <TextField
      className="field-input"
      name={`${condition.id}__field`}
      onChange={onChange}
      onClick={(event) => {
        event.stopPropagation();
      }}
      select
      value={value}
      variant="standard"
    >
      <MenuItem value="_" disabled>
        Choose Field
      </MenuItem>
      { fields.map((field, idx) => (
        <MenuItem key={idx} value={field.value}>
          { field.label }
        </MenuItem>
      )) }
    </TextField>
  );
}

function OperatorInput(props): ReactElement {
  const condition = props.condition || {};
  const properties = props.properties || {};
  const onChange = props.onChange;

  const key = toCamelCase(condition.field);
  const property = properties[key] || {};

  const options = [
    <MenuItem key="_" value="_" disabled>
      Choose Operator
    </MenuItem>,
    <MenuItem key="eq" value="eq">
      Equal
    </MenuItem>,
    <MenuItem key="neq" value="neq">
      Not Equal
    </MenuItem>
  ];

  if (property.type === 'number') {
    options.push(
      <MenuItem key="lt" value="lt">
        Less Than
      </MenuItem>,
      <MenuItem key="lte" value="lte">
        Less Than or Equal to
      </MenuItem>,
      <MenuItem key="gt" value="gt">
        Greater Than
      </MenuItem>,
      <MenuItem key="gte" value="gte">
        Greater Than or Equal to
      </MenuItem>
    );
  }

  return (
    <TextField
      className="operator-input"
      defaultValue=""
      disabled={!condition.field}
      name={`${condition.id}__operator`}
      select
      size="small"
      value={condition.operator || '_'}
      variant="standard"
      onChange={onChange}
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      { options }
    </TextField>
  );
}

function ValueInput(props): ReactElement {
  const condition = props.condition || {};
  const onChange = props.onChange;
  const properties = props.properties || {};

  const property = properties[condition.field] || {};

  if (property.oneOf) {
    return (
      <TextField
        className="value-input"
        name={`${condition.id}__value`}
        select
        value={condition.value || ''}
        variant="standard"
        size="small"
        onChange={onChange}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        { property.oneOf.map((value, idx) => (
          <MenuItem key={idx} value={value}>
            { value }
          </MenuItem>
        )) }
      </TextField>
    );
  }

  return (
    <TextField
      className="value-input"
      disabled={!(condition.field && condition.operator)}
      label="Value"
      name={`${condition.id}__value`}
      size="small"
      type="number"
      variant="standard"
      value={condition.value || ''}
      onChange={onChange}
    />
  );
}

function DeleteButton(props): ReactElement {
  const { condition } = props;

  return (
    <Icon
      className="cursor-pointer"
      data-action="remove"
      data-id={condition.id}
      fontSize="small"
      title="delete"
    >
      delete
    </Icon>
  );
}

export default FilterComponent;
